// Promotion Types and Interfaces

export enum PromotionType {
  DEPOSIT_BONUS = 1,      // ฝากเงินตามเงื่อนไข
  MINI_GAME = 4,          // Mini Game
  CREDIT_GIFT = 6,        // แจกเครดิต
}

export enum DepositConditionType {
  FIRST_DEPOSIT = 'first_deposit',      // โปรลูกค้าใหม่ (อัตโนมัติ)
  CONDITION_MATCH = 'condition_match',  // โปรลูกค้าเก่า (อัตโนมัติ)
  NORMAL = 'normal',                    // โปรฝากมือ
}

export enum MinigameConditionType {
  FIRST_DEPOSIT = 'first_deposit',      // Mini Game ลูกค้าใหม่ (อัตโนมัติ)
  CONDITION_MATCH = 'condition_match',  // Mini Game ลูกค้าเก่า (อัตโนมัติ)
  NORMAL = 'normal',                    // Mini ฝากมือ
}

export enum BonusType {
  FIXED = 'fixed',           // จำนวนเงินคงที่
  PERCENTAGE = 'percentage', // เปอร์เซ็นต์
}

export enum PromotionStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum PromotionMethod {
  ALL_MEMBER = 'all_member',
}

// กลุ่มโปรโมชัน (Promotion Category)
export enum PromotionCategory {
  NEW_MEMBER = 'new_member',           // ลูกค้าใหม่
  LOSSBACK = 'lossback',               // คืนยอดเสีย
  SPECIAL = 'special',                 // โปรโมชันพิเศษ
  DAILY = 'daily',                     // โปรประจำวัน
  WEEKLY = 'weekly',                   // โปรประจำสัปดาห์
  MONTHLY = 'monthly',                 // โปรประจำเดือน
  EVENT = 'event',                     // โปรกิจกรรม/เทศกาล
  VIP = 'vip',                         // โปร VIP
  AFFILIATE = 'affiliate',             // โปรแนะนำเพื่อน
  OTHER = 'other',                     // อื่นๆ
}

// Label สำหรับแสดงผล
export const PromotionCategoryLabels: Record<PromotionCategory, string> = {
  [PromotionCategory.NEW_MEMBER]: 'ลูกค้าใหม่',
  [PromotionCategory.LOSSBACK]: 'คืนยอดเสีย',
  [PromotionCategory.SPECIAL]: 'โปรโมชันพิเศษ',
  [PromotionCategory.DAILY]: 'โปรประจำวัน',
  [PromotionCategory.WEEKLY]: 'โปรประจำสัปดาห์',
  [PromotionCategory.MONTHLY]: 'โปรประจำเดือน',
  [PromotionCategory.EVENT]: 'โปรกิจกรรม/เทศกาล',
  [PromotionCategory.VIP]: 'โปร VIP',
  [PromotionCategory.AFFILIATE]: 'แนะนำเพื่อน',
  [PromotionCategory.OTHER]: 'อื่นๆ',
}

export enum ConditionOperator {
  GREATER_THAN = 'greater_than',
  EQUAL_TO = 'equal_to',
}

// Reward สำหรับ Mini Game
export interface Reward {
  bonus: number  // จำนวนโบนัส (บาท)
  weight: number // น้ำหนัก (%)
}

// Bonus Condition สำหรับ Promotion Type 1
export interface BonusCondition {
  condition: ConditionOperator  // greater_than, equal_to
  depositAmount: number          // ยอดฝาก (บาท)
  bonusPercent: number          // เปอร์เซ็นต์โบนัส (%)
  maxBonus: number              // โบนัสสูงสุด (บาท)
}

// Play Condition สำหรับ Mini Game
export interface PlayCondition {
  condition: ConditionOperator  // greater_than, equal_to
  depositAmount: number          // ยอดฝาก (บาท)
  playTimes: number             // จำนวนครั้งที่เล่นได้
}

// Promotion Interface
export interface Promotion {
  id: string
  objectId: string
  name: string
  code: string
  description?: string
  promotion_type: PromotionType
  promotion_method?: PromotionMethod
  category?: PromotionCategory          // กลุ่มโปรโมชัน
  deposit_condition_type?: DepositConditionType
  minigame_condition_type?: MinigameConditionType
  type?: BonusType
  amount?: number
  deposit_min?: number
  withdraw_limit?: number
  deposit_turnover_bonus_x?: number
  accepted_max_perday?: number
  startDate?: Date | string
  endDate?: Date | string
  status: PromotionStatus
  autoSent?: boolean
  returnCredit?: boolean
  userOnlyFirstDeposit?: boolean
  rewards?: Reward[]
  bonusConditions?: BonusCondition[]
  playConditions?: PlayCondition[]
  createdAt: string
  updatedAt: string
  deletedAt?: string
  
  // === Phase 1: New Fields (2026-02-02) ===
  
  // Limits
  max_usage_total?: number        // จำกัดการรับโปรทั้งหมด (0 = ไม่จำกัด)
  max_usage_per_member?: number   // จำกัดต่อสมาชิก (0 = ไม่จำกัด)
  budget_limit?: number           // งบประมาณรวม (0 = ไม่จำกัด)
  
  // Priority & Exclusivity
  priority?: number               // ลำดับความสำคัญ (สูง = ใช้ก่อน, default 0)
  is_exclusive?: boolean          // ห้ามใช้ร่วมกับโปรอื่น
  
  // Targeting
  member_groups?: string[]        // ObjectId[] ของ MemberGroup (ถ้าว่าง = all)
  
  // Visual
  image_url?: string              // URL รูปโปร
  
  // Analytics (read-only, updated by system)
  usage_count?: number            // จำนวนครั้งที่ใช้
  total_spent?: number            // งบที่ใช้ไป
}

// Query Parameters
export interface PromotionQueryParams {
  query?: string
  page?: number
  limit?: number
  sort?: {
    field?: string
    direction?: 'asc' | 'desc'
  }
}

// Promotion Form Data
export interface PromotionFormData {
  name: string
  code: string
  description?: string
  promotion_type: PromotionType
  promotion_method?: PromotionMethod
  category?: PromotionCategory
  deposit_condition_type?: DepositConditionType
  minigame_condition_type?: MinigameConditionType
  type?: BonusType
  amount?: number
  deposit_min?: number
  withdraw_limit?: number
  deposit_turnover_bonus_x?: number
  accepted_max_perday?: number
  startDate?: string
  endDate?: string
  status: PromotionStatus
  autoSent?: boolean
  returnCredit?: boolean
  userOnlyFirstDeposit?: boolean
  rewards?: Reward[]
  bonusConditions?: BonusCondition[]
  playConditions?: PlayCondition[]
  
  // === Phase 1: New Fields ===
  max_usage_total?: number
  max_usage_per_member?: number
  budget_limit?: number
  priority?: number
  is_exclusive?: boolean
  member_groups?: string[]
  image_url?: string
}









